'use client';

import { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'universal-cookie';

// A runaway client (e.g. an offline device stuck in a fetch retry loop) can emit
// thousands of identical errors from a single session, drowning out real signal in
// error-count monitors. The SDK's own limiter (3000 events/min) is not configurable,
// so cap duplicate error events per view here. The first occurrences still go through.
const MAX_ERRORS_PER_MESSAGE_PER_VIEW = 5;
// Bound the dedup map so a session emitting many distinct messages can't grow it forever.
const MAX_TRACKED_ERROR_KEYS = 1000;

const DataDogRUM = () => {
  useEffect(() => {
    const cookies = new Cookies();
    const isNativeApp = cookies.get('in_native_app');
    const errorCountByViewAndMessage = new Map<string, number>();
    const configuration = {
      applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID,
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'my-nerdwallet-next',
      env: process.env.NEXT_PUBLIC_DEPLOYABLE_ENV,
      version: process.env.NEXT_PUBLIC_APP_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 5,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      beforeSend: (event) => {
        event.context.userSource = isNativeApp ? 'nw-native-app' : 'nw-web';
        if (event.type === 'error') {
          const key = `${event.view?.id}:${event.error?.message}`;
          const count = (errorCountByViewAndMessage.get(key) ?? 0) + 1;
          if (
            count === 1 &&
            errorCountByViewAndMessage.size >= MAX_TRACKED_ERROR_KEYS
          ) {
            errorCountByViewAndMessage.clear();
          }
          errorCountByViewAndMessage.set(key, count);
          if (count > MAX_ERRORS_PER_MESSAGE_PER_VIEW) {
            return false;
          }
        }
        return true;
      },
    };
    if (
      process.env.NEXT_PUBLIC_DATADOG_APP_ID &&
      process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN
    ) {
      datadogRum.init(configuration);
    }
  }, []);

  return null;
};

export { datadogRum };
export default DataDogRUM;
